/** Mirrors credora360-api/app/core/rbac.py — used for Roles & Permissions UI. */

import {
  getRemotePermissionCatalog,
  getRemoteRoleOptions,
  getRemoteRolePermissions,
} from "./rbacAccess";

export function getRoleOptions() {
  const remote = getRemoteRoleOptions();
  if (remote?.length) {
    return remote;
  }
  return ROLE_OPTIONS;
}

export const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin" },
  { value: "branch_manager", label: "Branch Manager" },
  { value: "loan_officer", label: "Loan Officer" },
  { value: "credit_analyst", label: "Credit Analyst" },
  { value: "collections_officer", label: "Collections Officer" },
  { value: "cashier", label: "Cashier" },
  { value: "customer_support_agent", label: "Customer Support" },
  { value: "call_center_agent", label: "Call Center Agent" },
  { value: "auditor", label: "Auditor" },
  { value: "read_only_viewer", label: "Read-only Viewer" },
];

export const PERMISSION_LABELS = {
  "users.read": "View staff users",
  "users.manage": "Create & edit staff users",
  "borrowers.read": "View borrowers",
  "borrowers.create": "Register borrowers",
  "borrowers.manage": "Manage borrowers",
  "kyc.verify": "Verify KYC",
  "loans.read": "View loans",
  "loans.create": "Submit loan applications",
  "loans.manage": "Manage loan workflow",
  "loans.approve": "Approve loans",
  "loans.disburse": "Disburse loans",
  "collections.manage": "Collections workflow",
  "reports.read": "View reports & dashboards",
  "compliance.read": "View compliance",
  "compliance.manage": "Manage compliance records",
  "audit.read": "View audit logs",
  "crb.read": "View CRB data",
  "crb.inquiry": "Run CRB inquiries",
  "crb.manage": "Manage CRB submissions",
  "repayments.read": "View repayments",
  "repayments.create": "Record repayments",
  "communications.read": "View communications",
  "communications.send": "Send messages",
  "communications.manage": "Manage templates & campaigns",
  "call_center.read": "View call center",
  "call_center.manage": "Manage tickets & callbacks",
  "complaints.manage": "Manage complaints",
  "ai.read": "View AI insights",
  "ai.run": "Run AI scoring & models",
  "treasury.view": "View treasury & working capital",
  "treasury.manage": "Manage treasury accounts & allocations",
  "accounting.view": "View accounting & financial statements",
  "accounting.manage": "Manage accounting setup & journals",
};

export const ROLE_PERMISSIONS = {
  super_admin: Object.keys(PERMISSION_LABELS),
  branch_manager: [
    "users.read",
    "borrowers.read",
    "borrowers.create",
    "borrowers.manage",
    "kyc.verify",
    "loans.read",
    "loans.create",
    "loans.manage",
    "loans.approve",
    "loans.disburse",
    "collections.manage",
    "reports.read",
    "crb.read",
    "crb.inquiry",
    "crb.manage",
    "communications.read",
    "communications.send",
    "communications.manage",
    "call_center.read",
    "call_center.manage",
    "complaints.manage",
    "compliance.read",
    "compliance.manage",
    "ai.read",
    "ai.run",
    "treasury.view",
    "treasury.manage",
    "accounting.view",
    "accounting.manage",
  ],
  loan_officer: [
    "borrowers.read",
    "borrowers.create",
    "loans.read",
    "loans.create",
    "reports.read",
    "crb.read",
    "crb.inquiry",
    "ai.read",
    "treasury.view",
    "accounting.view",
  ],
  credit_analyst: [
    "borrowers.read",
    "kyc.verify",
    "loans.read",
    "loans.approve",
    "reports.read",
    "crb.read",
    "crb.inquiry",
    "ai.read",
    "ai.run",
    "treasury.view",
  ],
  collections_officer: [
    "borrowers.read",
    "loans.read",
    "collections.manage",
    "reports.read",
    "crb.read",
    "communications.read",
    "communications.send",
    "call_center.read",
    "call_center.manage",
  ],
  cashier: [
    "borrowers.read",
    "loans.read",
    "loans.disburse",
    "repayments.read",
    "repayments.create",
    "reports.read",
    "treasury.view",
    "accounting.view",
  ],
  customer_support_agent: [
    "borrowers.read",
    "loans.read",
    "reports.read",
    "call_center.read",
    "call_center.manage",
    "complaints.manage",
  ],
  call_center_agent: [
    "borrowers.read",
    "loans.read",
    "collections.manage",
    "reports.read",
    "communications.read",
    "communications.send",
    "call_center.read",
    "call_center.manage",
    "complaints.manage",
  ],
  auditor: [
    "users.read",
    "borrowers.read",
    "loans.read",
    "reports.read",
    "compliance.read",
    "audit.read",
    "crb.read",
    "repayments.read",
    "communications.read",
    "call_center.read",
    "complaints.manage",
    "treasury.view",
    "accounting.view",
  ],
  read_only_viewer: [
    "borrowers.read",
    "loans.read",
    "reports.read",
    "repayments.read",
    "ai.read",
    "treasury.view",
    "accounting.view",
  ],
};

/** Normalize API / stored role strings (e.g. super_admin). */
export function normalizeUserRole(role) {
  if (role == null || role === "") {
    return null;
  }
  const raw = typeof role === "string" ? role : String(role);
  return raw.trim().toLowerCase().replace(/\s+/g, "_");
}

export function isSuperAdmin(user) {
  return normalizeUserRole(user?.role) === "super_admin";
}

export function getActiveRolePermissions() {
  const remote = getRemoteRolePermissions();
  if (remote && Object.keys(remote).length > 0) {
    return remote;
  }
  return ROLE_PERMISSIONS;
}

export function getActivePermissionLabels() {
  const remote = getRemotePermissionCatalog();
  if (remote && Object.keys(remote).length > 0) {
    return remote;
  }
  return PERMISSION_LABELS;
}

export function permissionsForRole(role) {
  const normalized = normalizeUserRole(role);
  if (!normalized) {
    return [];
  }
  const labels = getActivePermissionLabels();
  if (normalized === "super_admin") {
    return Object.keys(labels);
  }
  return getActiveRolePermissions()[normalized] || [];
}

export function hasPermission(user, permission) {
  return permissionsForRole(user?.role).includes(permission);
}

export function hasAnyPermission(user, permissions) {
  if (isSuperAdmin(user)) {
    return true;
  }
  if (!permissions?.length) {
    return true;
  }
  const granted = permissionsForRole(user?.role);
  return permissions.some((p) => granted.includes(p));
}

export function roleLabel(role) {
  const normalized = normalizeUserRole(role);
  return getRoleOptions().find((r) => r.value === normalized)?.label || role;
}

export function canManageUsers(user) {
  return isSuperAdmin(user);
}

export function canRetryCommunications(user) {
  return hasPermission(user, "communications.manage");
}

export function canSendCommunications(user) {
  return hasPermission(user, "communications.send");
}

export function canManageCommunications(user) {
  return hasPermission(user, "communications.manage");
}
